import axios from "axios";
import { useMemo, useRef, useState } from "react";
import { Button, Form, Nav, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import GenericCrudPanel from "../../components/common/crud/GenericCrudPanel";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;

const ACTIVE_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const PAYROLL_STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "PROCESSED", label: "Processed" },
  { value: "PAID", label: "Paid" },
];

const numberValue = (value) => Number(value) || 0;

const calculateSalaryTotals = (form) => {
  const totalGrossSalary = [
    "basic_salary",
    "dearness_allowance",
    "hra",
    "conveyance",
    "medical_expense",
    "special_allowance",
    "other_allowances",
  ].reduce((total, field) => total + numberValue(form[field]), 0);
  const totalDeductions = [
    "provident_fund",
    "professional_tax",
    "esic",
    "other_deductions",
  ].reduce((total, field) => total + numberValue(form[field]), 0);

  return {
    ...form,
    total_gross_salary: totalGrossSalary,
    total_deductions: totalDeductions,
    net_salary: totalGrossSalary - totalDeductions,
  };
};

const MODULES = [
  {
    key: "salarystructure",
    title: "Salary Structure",
    endpoint: "/salarystructure/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "basic_salary", label: "Basic Salary", required: true, type: "number", step: "1" },
      { name: "dearness_allowance", label: "Dearness Allowance", type: "number", step: "1" },
      { name: "hra", label: "HRA", type: "number", step: "1" },
      { name: "conveyance", label: "Conveyance", type: "number", step: "1" },
      { name: "medical_expense", label: "Medical Expense", type: "number", step: "1" },
      { name: "special_allowance", label: "Special Allowance", type: "number", step: "1" },
      { name: "other_allowances", label: "Other Allowances", type: "number", step: "1" },
      { name: "provident_fund", label: "Provident Fund", type: "number", step: "1" },
      { name: "professional_tax", label: "Professional Tax", type: "number", step: "1" },
      { name: "esic", label: "ESIC", type: "number", step: "1" },
      { name: "other_deductions", label: "Other Deductions", type: "number", step: "1" },
      { name: "total_gross_salary", label: "Gross Salary", type: "number", step: "1", disabled: true },
      { name: "total_deductions", label: "Total Deductions", type: "number", step: "1", disabled: true },
      { name: "net_salary", label: "Net Salary", type: "number", step: "1", disabled: true },
      { name: "effective_from", label: "Effective From", required: true, type: "date" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "activeStatusOptions" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Basic Salary", selector: (row) => row.basic_salary ?? "-", sortable: true },
      { name: "Gross Salary", selector: (row) => row.total_gross_salary ?? "-", sortable: true },
      { name: "Total Deductions", selector: (row) => row.total_deductions ?? "-", sortable: true },
      { name: "Net Salary", selector: (row) => row.net_salary ?? "-", sortable: true },
      { name: "Effective From", selector: (row) => row.effective_from || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
    ],
    initialForm: {
      employee: "",
      basic_salary: "",
      dearness_allowance: "",
      conveyance: "",
      medical_expense: "",
      hra: "",
      special_allowance: "",
      other_allowances: "",
      provident_fund: "",
      professional_tax: "",
      esic: "",
      other_deductions: "",
      total_gross_salary: "",
      total_deductions: "",
      net_salary: "",
      effective_from: "",
      status: "ACTIVE",
    },
    calculateForm: calculateSalaryTotals,
  },
  {
    key: "payroll",
    title: "Payroll",
    endpoint: "/payroll/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "month", label: "Month", required: true, type: "number" },
      { name: "year", label: "Year", required: true, type: "number" },
      { name: "gross_salary", label: "Gross Salary", type: "number", step: "0.01" },
      { name: "total_deductions", label: "Total Deductions", type: "number", step: "0.01" },
      { name: "net_salary", label: "Net Salary", type: "number", step: "0.01" },
      { name: "days_present", label: "Days Present", type: "number" },
      { name: "days_on_leave", label: "Days On Leave", type: "number" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "payrollStatusOptions" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Month/Year", selector: (row) => `${row.month || "-"}/${row.year || "-"}`, sortable: true },
      { name: "Gross", selector: (row) => row.gross_salary ?? "-", sortable: true },
      { name: "Deductions", selector: (row) => row.total_deductions ?? "-", sortable: true },
      { name: "Net", selector: (row) => row.net_salary ?? "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
      {
        name: "Payslip",
        selector: (row) =>
          row.salaryslip ? (
            <a href={row.salaryslip} target="_blank" rel="noopener noreferrer">
              <i className="fa fa-file-pdf-o me-1"></i>
              View
            </a>
          ) : (
            "-"
          ),
      },
    ],
    initialForm: {
      employee: "",
      month: "",
      year: new Date().getFullYear(),
      gross_salary: "",
      total_deductions: "",
      net_salary: "",
      days_present: "",
      days_on_leave: "",
      status: "DRAFT",
      remarks: "",
    },
  },
];

async function loadOptionsForModule() {
  const employees = await axios.get(`${HR_BASE}/employee/`);
  return {
    employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
    activeStatusOptions: ACTIVE_STATUS_OPTIONS,
    payrollStatusOptions: PAYROLL_STATUS_OPTIONS,
  };
}

function PayrollManagement() {
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [activeTab, setActiveTab] = useState("payroll");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({ month: "", year: new Date().getFullYear() });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [payslipLoadingId, setPayslipLoadingId] = useState(null);
  const reloadRef = useRef(() => {});

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const activeModule = useMemo(() => MODULES.find((module) => module.key === activeTab) || MODULES[0], [activeTab]);

  const markPaid = async (row, reload) => {
    try {
      await axios.put(`${HR_BASE}/payroll/${row.id}/markpaid/`);
      toast.success("Payroll marked as paid.");
      reload();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to update payroll.");
      } else {
        toast.error(err?.response?.data?.error || "Error marking payroll as paid.");
      }
    }
  };

  const generatePayslip = async (row, reload) => {
    setPayslipLoadingId(row.id);
    try {
      await axios.post(`${HR_BASE}/payroll/${row.id}/generate_payslip/`);
      toast.success("Payslip generated.");
      reload();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to generate the payslip.");
      } else {
        toast.error(err?.response?.data?.error || "Error generating payslip.");
      }
    } finally {
      setPayslipLoadingId(null);
    }
  };

  const submitGenerate = async () => {
    if (!generateForm.month || !generateForm.year) {
      toast.error("Month and year are required.");
      return;
    }
    setGenerateLoading(true);
    try {
      const response = await axios.post(`${HR_BASE}/payroll/generate/`, generateForm);
      toast.success(`${(response.data || []).length} payroll record(s) generated.`);
      setShowGenerateModal(false);
      reloadRef.current();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to generate payroll.");
      } else {
        toast.error(err?.response?.data?.error || "Error generating payroll.");
      }
    } finally {
      setGenerateLoading(false);
    }
  };

  const renderExtraToolbarActions = (module, reload) => {
    if (module.key !== "payroll") return null;
    return (
      <Button
        className="btn btn-sm btn-info border-0 px-3 mx-2"
        onClick={() => {
          reloadRef.current = reload;
          setGenerateForm({ month: "", year: new Date().getFullYear() });
          setShowGenerateModal(true);
        }}
      >
        <i className="fa fa-cogs me-2"></i>
        Generate Payroll
      </Button>
    );
  };

  const renderExtraRowActions = (row, module, reload) => (
    module.key === "payroll" ? (
      <>
        {row.status !== "PAID" && (
          <Button className="btn btn-sm btn-success border-0 mx-1" title="Mark Paid" onClick={() => markPaid(row, reload)}>
            <i className="fa fa-check"></i>
          </Button>
        )}
        <Button
          className="btn btn-sm btn-info border-0 mx-1"
          title="Generate Payslip"
          disabled={payslipLoadingId === row.id}
          onClick={() => generatePayslip(row, reload)}
        >
          {payslipLoadingId === row.id ? (
            <Spinner as="span" animation="border" size="sm" />
          ) : (
            <i className="fa fa-file-pdf-o"></i>
          )}
        </Button>
      </>
    ) : null
  );

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="PayrollManagement"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-6 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">Payroll</span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <Nav variant="tabs" className="mb-3">
            {MODULES.map((module) => (
              <Nav.Item key={module.key}>
                <Nav.Link className="text-inox-green" active={activeTab === module.key} onClick={() => setActiveTab(module.key)}>
                  {module.title}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <GenericCrudPanel
            module={activeModule}
            baseUrl={HR_BASE}
            loadOptions={loadOptionsForModule}
            extraToolbarActions={renderExtraToolbarActions}
            extraRowActions={renderExtraRowActions}
            extraRowActionsWidth="200px"
          />
        </div>
      </div>

      <div className="modal fade show" style={{ display: showGenerateModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Generate Payroll</h5>
              <button type="button" className="btn-close" onClick={() => setShowGenerateModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Label>Month *</Form.Label>
              <Form.Control type="number" min="1" max="12" value={generateForm.month} onChange={(e) => setGenerateForm((prev) => ({ ...prev, month: e.target.value }))} />
              <Form.Label className="mt-2">Year *</Form.Label>
              <Form.Control type="number" value={generateForm.year} onChange={(e) => setGenerateForm((prev) => ({ ...prev, year: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitGenerate}>
                {generateLoading ? <Spinner as="span" animation="border" size="sm" /> : "Generate"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default PayrollManagement;
